import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Alert,
  Divider,
  Button,
  Stack,
} from '@mui/material';
import {
  UserIcon,
  KeyIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import PhoneField from '../components/common/PhoneField';
import { COUNTRY_CODES, splitE164 } from '../utils/countryCodes';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';

const UserProfilePage = () => {
  const user = useAuthStore((state) => state.user);
  // The business number given at signup. Read from the tenant, not the
  // user: it identifies the business, and every teammate sees the same one.
  const [businessPhone, setBusinessPhone] = useState(null);
  const [phoneDraft, setPhoneDraft] = useState('');
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneMeta, setPhoneMeta] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/billing/self/business-phone')
      .then(({ data }) => {
        if (cancelled) return;
        setBusinessPhone(data.business_phone || '');
        setPhoneMeta(data);
      })
      // Tenant admins only. A member seeing their profile is not an error,
      // so the section simply does not appear for them.
      .catch(() => { if (!cancelled) setBusinessPhone(null); });
    return () => { cancelled = true; };
  }, []);

  // Derived, never stored. One fact, read from the number itself.
  //
  // A dial code is not a country: +1 is the US AND Canada, and splitE164
  // returns whichever is listed first. Naming a country we cannot identify
  // would tell a Canadian business it is American. Show the code instead
  // unless exactly one country owns it.
  const phoneCountry = useMemo(() => {
    const { dial } = splitE164(businessPhone || '');
    if (!dial) return null;
    const owners = COUNTRY_CODES.filter((c) => c.dial === dial);
    return { dial, name: owners.length === 1 ? owners[0].name : null };
  }, [businessPhone]);

  const savePhone = async () => {
    setPhoneBusy(true);
    // Claim the scope before writing either message. error/success are
    // shared across the page's three sections, so an unscoped write is
    // invisible at best and lands under the password or email card at
    // worst, depending on which action ran last.
    setFeedbackScope('business_phone');
    setError('');
    setSuccess('');
    try {
      const { data } = await api.put('/billing/self/business-phone', {
        business_phone: phoneDraft.trim(),
      });
      setBusinessPhone(data.business_phone);
      setPhoneMeta(data);
      setEditingPhone(false);
      setSuccess(data.currency_changed
        ? `Business number updated. Your billing currency is now ${data.currency}.`
        : 'Business number updated.');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not update the business number');
    } finally {
      setPhoneBusy(false);
    }
  };
  const updateUserProfile = useAuthStore((state) => state.updateUserProfile);
  const isSuperadmin = Boolean(
    user?.role === 'superadmin' || user?.is_superuser || user?.is_superadmin_actor
  );

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordCode, setPasswordCode] = useState('');
  const [isChanging, setIsChanging] = useState(false);
  const [isSendingPasswordCode, setIsSendingPasswordCode] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [currentEmailCode, setCurrentEmailCode] = useState('');
  const [isSendingCurrentEmailCode, setIsSendingCurrentEmailCode] = useState(false);
  const [emailStep, setEmailStep] = useState('email');
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [feedbackScope, setFeedbackScope] = useState('');

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setFeedbackScope('password');
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    if (oldPassword === newPassword) {
      setError('New password must be different from old password');
      return;
    }

    if (!/^\d{6}$/.test(passwordCode.trim())) {
      setError('Enter the six-digit verification code');
      return;
    }

    setIsChanging(true);
    try {
      await api.post('/auth/change-password', {
        old_password: oldPassword,
        new_password: newPassword,
        verification_code: passwordCode.trim(),
      });
      setSuccess('Password changed successfully');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordCode('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setIsChanging(false);
    }
  };

  const handleRequestPasswordCode = async () => {
    setFeedbackScope('password');
    setError('');
    setSuccess('');
    if (!user?.email) {
      setError('Add a verified account email before changing your password');
      return;
    }
    setIsSendingPasswordCode(true);
    try {
      // Signed-in, so not the public sign-in endpoint (which needs Turnstile).
      await api.post('/auth/me/request-code');
      setSuccess(`Verification code sent to ${user.email}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setIsSendingPasswordCode(false);
    }
  };

  const handleRequestEmailCode = async (e) => {
    e.preventDefault();
    setFeedbackScope('email');
    setError('');
    setSuccess('');
    const normalized = newEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
      setError('Enter a valid email address');
      return;
    }
    if (normalized === String(user?.email || '').trim().toLowerCase()) {
      setError('Enter a different email address');
      return;
    }
    setIsUpdatingEmail(true);
    try {
      await api.post('/auth/email-change/request-code', { new_email: normalized });
      setNewEmail(normalized);
      setEmailStep('code');
      setSuccess(`Verification code sent to ${normalized}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handleRequestCurrentEmailCode = async () => {
    setFeedbackScope('email');
    setError('');
    setSuccess('');
    if (!user?.email) {
      setError('Add a verified account email before changing your email');
      return;
    }
    setIsSendingCurrentEmailCode(true);
    try {
      await api.post('/auth/me/request-code');
      setSuccess(`Verification code sent to ${user.email}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setIsSendingCurrentEmailCode(false);
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setFeedbackScope('email');
    setError('');
    setSuccess('');
    if (!/^\d{6}$/.test(emailCode.trim())) {
      setError('Enter the six-digit verification code');
      return;
    }
    // Superadmins must also prove they still control the *current* email --
    // owning the new address alone is not enough for an account this
    // powerful (see handleVerifyEmail's backend counterpart for why).
    if (isSuperadmin && !/^\d{6}$/.test(currentEmailCode.trim())) {
      setError('Enter the six-digit code sent to your current email');
      return;
    }
    setIsUpdatingEmail(true);
    try {
      const response = await api.post('/auth/email-change/verify-code', {
        new_email: newEmail,
        code: emailCode.trim(),
        ...(isSuperadmin ? { current_email_code: currentEmailCode.trim() } : {}),
      });
      updateUserProfile({ email: response.data.email });
      setSuccess('Email updated successfully');
      setNewEmail('');
      setEmailCode('');
      setCurrentEmailCode('');
      setEmailStep('email');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update email');
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const getRoleBadge = () => {
    const role = user?.tenant_role || user?.role || 'user';
    const colors = {
      superadmin: 'bg-purple-100 text-purple-800',
      owner: 'bg-blue-100 text-blue-800',
      admin: 'bg-indigo-100 text-indigo-800',
      member: 'bg-gray-100 text-gray-800',
      user: 'bg-gray-100 text-gray-800',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[role] || colors.user}`}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </span>
    );
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          My Profile
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {isSuperadmin
            ? 'View your account details, update your email, and change your password'
            : 'View your account details and update your verified email'}
        </Typography>
      </Box>

      {/* User Info Card */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <UserIcon className="h-8 w-8 text-blue-600" />
          <Typography variant="h6" fontWeight="bold">
            Account Details
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Username
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {user?.username || '-'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Email
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {user?.email || '-'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Role
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              {getRoleBadge()}
            </Box>
          </Grid>
          {user?.tenant_name && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Organization
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {user.tenant_name}
              </Typography>
            </Grid>
          )}

          {businessPhone !== null && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Business number
              </Typography>

              {feedbackScope === 'business_phone' && error && (
                <Alert severity="error" sx={{ my: 1 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              {feedbackScope === 'business_phone' && success && (
                <Alert severity="success" sx={{ my: 1 }} onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              {!editingPhone ? (
                <>
                  <Typography variant="body1" fontWeight="medium">
                    {businessPhone || 'Not set'}
                    {phoneCountry && (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {` \u00b7 ${phoneCountry.name || phoneCountry.dial}`}
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {phoneMeta?.currency
                      ? `Billed in ${phoneMeta.currency}`
                      : 'Used to decide your billing currency'}
                  </Typography>
                  <Button
                    size="small"
                    sx={{ mt: 0.5, px: 0 }}
                    onClick={() => { setPhoneDraft(businessPhone || ''); setEditingPhone(true); }}
                  >
                    {businessPhone ? 'Change' : 'Add number'}
                  </Button>
                </>
              ) : (
                <Stack spacing={1} sx={{ mt: 0.5 }}>
                  {/* The shared picker. A plain input here re-created the
                      raw-phone path PhoneField exists to remove: it was
                      capped at 20 characters, and a pasted
                      "+1 (415) 555-0123" exceeds that once spaces and
                      brackets are counted. */}
                  <PhoneField
                    id="profile-business-phone"
                    value={phoneDraft}
                    onChange={setPhoneDraft}
                    disabled={phoneBusy}
                  />
                  {/* Once a currency is frozen -- after a payment, or a
                      back-office decision -- changing the number must not
                      silently move what someone is billed in. Say so. */}
                  {phoneMeta?.currency_locked && (
                    <Typography variant="caption" color="text.secondary">
                      Your billing currency is already set and will not change.
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="contained" disabled={phoneBusy} onClick={savePhone}>
                      {phoneBusy ? 'Saving...' : 'Save'}
                    </Button>
                    <Button size="small" disabled={phoneBusy} onClick={() => setEditingPhone(false)}>
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Superadmins retain code-gated password management. */}
      {isSuperadmin && (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <KeyIcon className="h-8 w-8 text-blue-600" />
          <Typography variant="h6" fontWeight="bold">
            Change Password
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />

        {feedbackScope === 'password' && error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {feedbackScope === 'password' && success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            A fresh code sent to <strong>{user?.email || 'your verified email'}</strong> is required.
          </Typography>
          <button
            type="button"
            onClick={handleRequestPasswordCode}
            disabled={isSendingPasswordCode || !user?.email}
            className="shrink-0 min-h-[38px] px-4 border border-blue-600 text-blue-700 rounded-md hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            {isSendingPasswordCode ? 'Sending...' : 'Send Code'}
          </button>
        </Box>

        <form onSubmit={handleChangePassword}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={2.4}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Password
              </label>
              <input
                type="password"
                required
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="Enter your current password"
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="Enter new password"
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="Confirm new password"
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Verification Code
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                required
                value={passwordCode}
                onChange={(e) => setPasswordCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoComplete="one-time-code"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm tracking-widest"
                placeholder="000000"
              />
            </Grid>
            <Grid item xs={12} md={2.4}>
              <button
                type="submit"
                disabled={isChanging}
                className="w-full min-h-[44px] md:min-h-[38px] px-6 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
              >
                {isChanging ? 'Changing...' : 'Change Password'}
              </button>
            </Grid>
          </Grid>
        </form>
      </Paper>
      )}

      {/* Every account can replace its email only after proving ownership of
          the proposed address with a single-use code. */}
      <Paper sx={{ p: 3, mt: isSuperadmin ? 4 : 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <EnvelopeIcon className="h-8 w-8 text-blue-600" />
          <Typography variant="h6" fontWeight="bold">
            Update Email
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />

        {feedbackScope === 'email' && error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {feedbackScope === 'email' && success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {emailStep === 'email' ? (
          <form onSubmit={handleRequestEmailCode}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} md={8}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Email
                </label>
                <input
                  type="email"
                  required
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  autoComplete="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Enter your new email"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <button
                  type="submit"
                  disabled={isUpdatingEmail}
                  className="w-full min-h-[44px] md:min-h-[38px] px-6 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                >
                  {isUpdatingEmail ? 'Sending...' : 'Send Verification Code'}
                </button>
              </Grid>
            </Grid>
          </form>
        ) : (
          <form onSubmit={handleVerifyEmail}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the six-digit code sent to <strong>{newEmail}</strong>.
            </Typography>
            {isSuperadmin && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  A superadmin change also requires a fresh code sent to your current email{' '}
                  <strong>{user?.email || 'on file'}</strong>.
                </Typography>
                <button
                  type="button"
                  onClick={handleRequestCurrentEmailCode}
                  disabled={isSendingCurrentEmailCode || !user?.email}
                  className="shrink-0 min-h-[38px] px-4 border border-blue-600 text-blue-700 rounded-md hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {isSendingCurrentEmailCode ? 'Sending...' : 'Send Code'}
                </button>
              </Box>
            )}
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} md={5}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Verification Code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  value={emailCode}
                  onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoComplete="one-time-code"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm tracking-widest"
                  placeholder="000000"
                />
              </Grid>
              {isSuperadmin && (
                <Grid item xs={12} md={5}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Email Code
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    required
                    value={currentEmailCode}
                    onChange={(e) => setCurrentEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoComplete="one-time-code"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm tracking-widest"
                    placeholder="000000"
                  />
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                <button
                  type="submit"
                  disabled={isUpdatingEmail}
                  className="w-full min-h-[44px] md:min-h-[38px] px-6 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                >
                  {isUpdatingEmail ? 'Updating...' : 'Verify and Update'}
                </button>
              </Grid>
              <Grid item xs={12} md={3}>
                <button
                  type="button"
                  onClick={() => { setEmailStep('email'); setEmailCode(''); setCurrentEmailCode(''); setError(''); setSuccess(''); }}
                  className="w-full min-h-[44px] md:min-h-[38px] px-4 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm font-medium"
                >
                  Change Email
                </button>
              </Grid>
            </Grid>
          </form>
        )}
      </Paper>
    </Container>
  );
};

export default UserProfilePage;
